import React from "react";
import { ExternalLink, UserPlus } from "lucide-react";
import type { CopyTradeLeader } from "@/api/copy-trade";

interface CopyTradeLeaderCardProps {
  leader: CopyTradeLeader;
  isSubscribed?: boolean;
  onViewDetails: () => void;
  onSubscribe: () => void;
}

const isFiniteNumber = (value: number | null | undefined): value is number =>
  value != null && Number.isFinite(value);

const formatPercent = (value: number | null | undefined) =>
  isFiniteNumber(value) ? `${value.toFixed(2)}%` : "—";

const formatNumber = (value: number | null | undefined) =>
  isFiniteNumber(value)
    ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : "—";

export const CopyTradeLeaderCard: React.FC<CopyTradeLeaderCardProps> = ({
  leader,
  isSubscribed,
  onViewDetails,
  onSubscribe,
}) => {
  return (
    <div className="bg-secondary border border-border rounded-2xl p-5 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        {leader.avatarUrl ? (
          <img
            src={leader.avatarUrl}
            alt={leader.nickname}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-tertiary flex items-center justify-center text-lg font-bold">
            {leader.nickname.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-text-primary truncate">
            {leader.nickname}
          </h3>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-text-secondary text-xs">ROI (30D)</p>
          <p className="font-medium text-text-primary">
            {formatPercent(leader.roi)}
          </p>
        </div>
        <div>
          <p className="text-text-secondary text-xs">PnL</p>
          <p className="font-medium text-text-primary">
            ${formatNumber(leader.pnl)}
          </p>
        </div>
        <div>
          <p className="text-text-secondary text-xs">Win rate</p>
          <p className="font-medium text-text-primary">
            {formatPercent(leader.winRate)}
          </p>
        </div>
        <div>
          <p className="text-text-secondary text-xs">MDD</p>
          <p className="font-medium text-text-primary">
            {formatPercent(leader.mdd)}
          </p>
        </div>
      </div>

      <div className="flex gap-2 mt-auto">
        <button
          type="button"
          onClick={onViewDetails}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-border text-text-primary hover:bg-tertiary transition-colors text-sm font-medium"
        >
          <ExternalLink className="w-4 h-4" />
          View details
        </button>
        <button
          type="button"
          onClick={onSubscribe}
          disabled={isSubscribed}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-accent text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <UserPlus className="w-4 h-4" />
          {isSubscribed ? "Subscribed" : "Subscribe"}
        </button>
      </div>
    </div>
  );
};
